"use client";

import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import * as React from "react";

type MenuItem = {
  title: string;
  href: string;
};

type NavigationMenuProps = {
  items: MenuItem[];
  className?: string;
};

export function FloatingNavigationMenu({
  items,
  className,
}: NavigationMenuProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const itemsRef = React.useRef<(HTMLLIElement | null)[]>([]);
  const [dimensions, setDimensions] = React.useState<{
    width: number;
    left: number;
    top: number;
  }>({
    width: 0,
    left: 0,
    top: 0,
  });

  // Update dimensions when active index changes
  React.useEffect(() => {
    if (activeIndex !== null) {
      const activeItem = itemsRef.current[activeIndex];
      if (activeItem) {
        const rect = activeItem.getBoundingClientRect();
        const parentRect =
          activeItem.parentElement?.getBoundingClientRect() || rect;

        setDimensions({
          width: rect.width,
          left: rect.left - parentRect.left,
          top: rect.top - parentRect.top,
        });
      }
    }
  }, [activeIndex]);

  // Effect to handle body scroll lock when mobile menu is open
  React.useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileOpen]);

  const handleMouseLeave = () => {
    setActiveIndex(null);
  };

  return (
    <>
      <nav className={cn("relative z-50 w-full py-6", className)}>
        <div className="mx-auto flex justify-center">
          <div className="bg-muted rounded-3xl px-6 py-2.5 border">
            <div className="flex items-center justify-between gap-8">
              {/* Logo */}
              <Link href="/" className="flex items-center relative w-7 h-7">
                <Image
                  src="/images/cnc.png"
                  alt="Protype Logo"
                  width={28}
                  height={28}
                  className="object-contain"
                  style={{
                    filter: "brightness(0.85) contrast(1.25) saturate(1.2)",
                  }}
                  priority
                />
              </Link>

              {/* Hamburger for mobile */}
              <button
                onClick={() => setMobileOpen(true)}
                className="md:hidden p-2 focus:outline-none"
                aria-label="Open menu"
              >
                <svg
                  className="h-6 w-6 text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>

              {/* Navigation Items - desktop */}
              <ul
                className="hidden md:flex relative items-center gap-6 md:gap-8"
                onMouseLeave={handleMouseLeave}
              >
                {activeIndex !== null && (
                  <motion.div
                    className="absolute rounded-full bg-background/80"
                    initial={{ opacity: 0 }}
                    animate={{
                      width: dimensions.width,
                      left: dimensions.left,
                      top: dimensions.top,
                      height: "100%",
                      opacity: 1,
                    }}
                    exit={{ opacity: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: 350,
                      damping: 30,
                    }}
                    layoutId="hoverBackground"
                  />
                )}

                {items.map((item, index) => (
                  <li
                    key={item.title}
                    ref={(el) => {
                      if (el) itemsRef.current[index] = el;
                    }}
                    className="relative"
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <Link
                      href={item.href}
                      className={cn(
                        "relative block rounded-full px-3 py-1.5 text-sm font-medium transition-colors z-10",
                        activeIndex === index
                          ? "text-primary"
                          : "text-primary/70"
                      )}
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Sign In - desktop */}
              <Link
                href="/sign-in"
                className="hidden md:inline-block rounded-full border border-primary px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="fixed inset-0 z-50 bg-background"
        >
          <motion.div
            className="h-full flex flex-col"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Link href="/" className="flex items-center relative w-7 h-7">
                  <Image
                    src="/images/cnc.png"
                    alt="Protype Logo"
                    width={28}
                    height={28}
                    className="object-contain"
                    style={{
                      filter: "brightness(0.85) contrast(1.25) saturate(1.2)",
                    }}
                    priority
                  />
                </Link>
              </motion.div>
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                onClick={() => setMobileOpen(false)}
                className="p-2 focus:outline-none"
                aria-label="Close menu"
              >
                <svg
                  className="h-6 w-6 text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </motion.button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 flex flex-col items-center justify-center gap-8 px-4">
              {items.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
                >
                  <Link
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="text-2xl font-semibold text-primary hover:text-primary/90 transition-colors"
                  >
                    {item.title}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 + items.length * 0.1 }}
              >
                <Link
                  href="/sign-in"
                  onClick={() => setMobileOpen(false)}
                  className="mt-4 px-6 py-3 border-2 border-primary rounded-full text-primary font-semibold text-lg hover:bg-primary hover:text-primary-foreground"
                >
                  Sign In
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
